import { LMIProofDoc } from '~/screens/lmi-proof-docs-upload';
import { AgreeToTermsResponse } from '~/services/agree-to-terms';
import { CreateFunnelPacketResponse } from '~/services/create-funnel-packet';
import { CreateMeterResponse } from '~/services/create-meter';
import { CreatePaymentHoldResponse } from '~/services/create-payment-hold';
import { CreateProductLeadResponse } from '~/services/create-product-lead';
import { CreateUserResponse } from '~/services/create-user';
import { DeleteBillUploadResponse } from '~/services/delete-bill-upload';
import { DeleteLMIProofDocUploadResponse } from '~/services/delete-lmi-proof-doc-upload';
import { FetchBillingExperienceOptionsResponse } from '~/services/fetch-billing-experience-options';
import { FetchBillpayEligibilityResponse } from '~/services/fetch-billpay-eligibility';
import { FetchContractLanguageResponse } from '~/services/fetch-contract-version-language';
import { FetchDocuSignEnvelopesResponse } from '~/services/fetch-docu-sign-envelopes';
import { FetchEnrollmentOptionsResponse } from '~/services/fetch-enrollment-option';
import { FetchExistingPaymentMethodsResponse } from '~/services/fetch-existing-payment-methods';
import { FetchExistingPlansResponse } from '~/services/fetch-existing-plans';
import { FetchExistingUtilityAccountsResponse } from '~/services/fetch-existing-utility-accounts';
import { FetchFunnelPacketResponse } from '~/services/fetch-funnel-packet';
import { FetchIsInLmiZoneResponse } from '~/services/fetch-is-in-lmi-zone';
import { FetchLMIProofDocsResponse } from '~/services/fetch-lmi-proof-docs';
import { FetchMembershipPromoCodeResponse } from '~/services/fetch-membership-promo-code';
import { FetchNyLmiZoneOptionsResponse } from '~/services/fetch-ny-dac-ez-zone';
import { FetchPlanTemplateResponse } from '~/services/fetch-plan-template';
import { FetchStripeTokenResponse } from '~/services/fetch-stripe-token';
import { FetchUtilityResponse } from '~/services/fetch-utility';
import { FetchUtilityAccountResponse } from '~/services/fetch-utility-account';
import { FetchUtilityConnectTokenResponse } from '~/services/fetch-utility-connect-token';
import { FetchVnmTermsResponse } from '~/services/fetch-vnm-terms';
import { FetchWaitlistOptionsResponse } from '~/services/fetch-waitlist-options';
import { ListenForPaymentResponse } from '~/services/listen-for-payment';
import { RedirectToDashboardResponse } from '~/services/redirect-to-dashboard';
import { RequestDocuSignEnvelopeResponse } from '~/services/request-docu-sign-envelope';
import { SendDocusignInstantAlertResponse } from '~/services/send-docusign-instant-alert';
import { SendInstantAlertResponse } from '~/services/send-instant-alert';
import { SendTelesalesEmailServiceResponse } from '~/services/send-telesales-email';
import { SignUpUserResponse } from '~/services/sign-up-user';
import { UpdateAmerenCredentialResponse } from '~/services/update-ameren-credential';
import { UpdatePlanPaymentMethodResponse } from '~/services/update-plan-payment-method';
import { UpdateUserPreferencesResponse } from '~/services/update-user-preferences';
import { UpdateUtilityAccountResponse } from '~/services/update-utility-account';
import { UpdateUtilityAccountDetailResponse } from '~/services/update-utility-account-detail';
import { UploadBillImageResponse } from '~/services/upload-bill-image';
import { UploadContractsResponse } from '~/services/upload-contracts';
import { EnrollmentDataSource, BillingDataSource, CustomPlan } from '~/typings/context';
import {
  DocuSignClickwrap,
  FunnelTrack,
  GetUserDocuSignEnvelopesQuery,
  UpdateConsolidatedUtilityAccountResponse,
} from '~/typings/graphql';
import { UTILITY_IDS } from '../constants';
import { addFileProps } from '../files';
import { TEST_TERMS } from './terms';
import { fetchUtilityResponse, TEST_STATES, TEST_UTILITIES } from './utilities';
import { TEST_UTILITY_ACCOUNTS } from './utility-accounts';

export { randomAccountNumber } from './utilities';

const lmiDocTypes = [
  'Comfort Partners',
  'Lifeline USAC',
  'Lifeline Utility Assistance Program',
  'LIHEAP',
  'Medicaid',
  'PAGE',
  'Qualifying Income',
  'Section 8',
  'SNAP',
  'Universal Service Fund',
];

const lmiDocSubmit = {
  context: {
    funnelPacket: {
      user: {
        id: 1,
        promoCode: undefined,
        firstName: 'Freddie',
        lastName: 'Flexington',
        email: 'freddie@example.com',
        phoneNumber: '1234567890',
        zipCode: TEST_UTILITIES.natGrid.zipCode,
        phone: '1234567890',
      },
      utility: fetchUtilityResponse('natGrid'),
      utilityAccount: {
        id: 4,
        accountNumber: '1234567890',
      },
      utilityCredential: {
        id: 5,
        verificationStatus: 'verified',
      },
      inviteCode: 'testCode',
    },
    docs: [{ documentType: 'SNAP' }],
    promoCode: 'spanish123',
    documentTypes: lmiDocTypes,
  },
  event: { type: 'SUBMIT_DOCS' },
};

const lmiDocSkip = {
  context: {
    funnelPacket: {
      user: {
        id: 1,
        promoCode: undefined,
        firstName: 'Freddie',
        lastName: 'Flexington',
        email: 'freddie@example.com',
        phoneNumber: '1234567890',
        zipCode: TEST_UTILITIES.natGrid.zipCode,
        phone: '1234567890',
      },
      utility: fetchUtilityResponse('natGrid'),
      utilityAccount: {
        id: 4,
        accountNumber: '1234567890',
      },
      utilityCredential: {
        id: 5,
        verificationStatus: 'verified',
      },
      inviteCode: 'testCode',
    },
    docs: [],
    promoCode: 'spanish123',
    documentTypes: lmiDocTypes,
  },
  event: { type: 'done.invoke.(machine).skipping:invocation[0]' },
};

const lmiUploadDoc = {
  context: {
    funnelId: 'goSolar',
    utilitySelection: {
      displayName: 'Eversource NSTAR',
      funnelTrack: 'solar',
      id: 2286,
      state: {
        abbreviation: 'MA',
        id: 22,
        name: 'Massachusetts',
      },
    },
    queryParams: {
      unparsedQueryParams: {
        nonLmi: false,
      },
    },
  },
};

const nonLmiUploadDoc = {
  context: {
    funnelId: 'goSolar',
    queryParams: {
      unparsedQueryParams: {
        nonLmi: true,
      },
    },
  },
};

const fetchFunnelPacketWtPayment: FetchFunnelPacketResponse = {
  nonLmi: true,
  inviteCode: 'testCode',
  account: {
    id: 2,
    businessName: undefined,
    customerType: 'residential',
  },
  customPlan: {
    id: 3,
    paymentMethodId: 123,
  },
  planTemplate: {
    id: 4,
    billingExperienceType: 'acb',
  },
  signedContractIds: [10, 11],
  user: {
    id: 1,
    promoCode: undefined,
    firstName: 'Freddie',
    lastName: 'Flexington',
    email: 'freddie@example.com',
    phoneNumber: '1234567890',
    zipCode: TEST_UTILITIES.natGrid.zipCode,
    phone: '1234567890',
    originationSource: {
      originationSourceType: '',
      funnel: '',
    },
  },
  utility: fetchUtilityResponse('natGrid'),
  utilityAccount: {
    id: 4,
  },
  utilityCredential: {
    id: 5,
    verificationStatus: 'verified', // TODO: not a real status
  },
};

const fetchFunnelPacketUcb: FetchFunnelPacketResponse = {
  nonLmi: true,
  inviteCode: 'testCode',
  account: {
    id: 2,
    businessName: undefined,
    customerType: 'residential',
  },
  customPlan: {
    id: 3,
    paymentMethodId: 123,
  },
  planTemplate: {
    id: 4,
    billingExperienceType: 'ucb',
  },
  signedContractIds: [10, 11],
  user: {
    id: 1,
    promoCode: undefined,
    firstName: 'Freddie',
    lastName: 'Flexington',
    email: 'freddie@example.com',
    phoneNumber: '1234567890',
    zipCode: TEST_UTILITIES.natGrid.zipCode,
    phone: '1234567890',
    originationSource: {
      originationSourceType: '',
      funnel: '',
    },
  },
  utility: fetchUtilityResponse('natGrid'),
  utilityAccount: {
    id: 4,
  },
  utilityCredential: {
    id: 5,
    verificationStatus: 'verified', // TODO: not a real status
  },
};
const funnelPacketBusiness: FetchFunnelPacketResponse = {
  nonLmi: true,
  inviteCode: 'testCode',
  account: {
    id: 2,
    businessName: undefined,
    customerType: 'residential',
  },
  customPlan: {
    id: 3,
    paymentMethodId: null,
  },
  planTemplate: {
    id: 4,
  },
  signedContractIds: [10, 11],
  user: {
    id: 1,
    promoCode: undefined,
    firstName: 'Freddie',
    lastName: 'Flexington',
    email: 'freddie@example.com',
    zipCode: TEST_UTILITIES.natGrid.zipCode,
    phone: '1234567890',
    originationSource: {
      originationSourceType: '',
      funnel: 'business',
    },
  },
  utility: {
    displayName: 'Eversource NSTAR',
    funnelTrack: FunnelTrack.Solar,
    id: 2286,
    state: { abbreviation: 'MA', id: 22, name: 'Massachusetts' },
  },
  utilityAccount: {
    id: 4,
  },
  utilityCredential: {
    id: 5,
    verificationStatus: 'verified', // TODO: not a real status
  },
};

const billingOptionsPayload = {
  enrollmentOptions: ['utility_connect'],
  enrollmentSelection: 'utility_connect',
  funnelPacket: funnelPacketBusiness,
  queryParams: {
    unparsedQueryParams: {},
    utmParams: {},
  },
  utilitySelection: {
    displayName: 'Eversource NSTAR',
    funnelTrack: 'solar',
    id: 2286,
    state: {
      abbreviation: 'MA',
      id: 22,
      name: 'Massachusetts',
    },
  },
  zipCode: '01701',
};

const fetchFunnelPacketAmerenMo: FetchFunnelPacketResponse = {
  nonLmi: true,
  inviteCode: 'testCode',
  account: {
    id: 2,
    businessName: undefined,
    customerType: 'residential',
  },
  customPlan: {
    id: 3,
    paymentMethodId: 123,
  },
  planTemplate: {
    id: 4,
    billingExperienceType: 'ucb',
  },
  signedContractIds: [10, 11],
  user: {
    id: 1,
    promoCode: undefined,
    firstName: 'Freddie',
    lastName: 'Flexington',
    email: 'freddie@example.com',
    phoneNumber: '1234567890',
    zipCode: TEST_UTILITIES.amerenMo.zipCode,
    phone: '1234567890',
    originationSource: {
      originationSourceType: '',
      funnel: '',
    },
  },
  utility: fetchUtilityResponse('amerenMo'),
  utilityAccount: {
    id: 4,
  },
  utilityCredential: {
    id: 5,
    verificationStatus: 'verified', // TODO: not a real status
  },
};
const funnelPacketLMIUsers: FetchFunnelPacketResponse = {
  nonLmi: false,
  inviteCode: 'testCode',
  account: {
    id: 2,
    businessName: undefined,
    customerType: 'residential',
  },
  customPlan: {
    id: 3,
    paymentMethodId: null,
  },
  planTemplate: {
    id: 4,
  },
  signedContractIds: [10, 11],
  user: {
    id: 1,
    promoCode: undefined,
    firstName: 'Freddie',
    lastName: 'Flexington',
    email: 'freddie@example.com',
    phoneNumber: '1234567890',
    zipCode: TEST_UTILITIES.natGrid.zipCode,
    phone: '1234567890',
    originationSource: {
      originationSourceType: '',
      funnel: '',
    },
  },
  utility: fetchUtilityResponse('natGrid'),
  utilityAccount: {
    id: 4,
  },
  utilityCredential: {
    id: 5,
    verificationStatus: 'verified', // TODO: not a real status
  },
};

const funnelPacket: FetchFunnelPacketResponse = {
  nonLmi: true,
  inviteCode: 'testCode',
  account: {
    id: 2,
    businessName: undefined,
    customerType: 'residential',
  },
  customPlan: {
    id: 3,
    paymentMethodId: null,
  },
  planTemplate: {
    id: 4,
  },
  signedContractIds: [10, 11],
  user: {
    id: 1,
    promoCode: undefined,
    firstName: 'Freddie',
    lastName: 'Flexington',
    email: 'freddie@example.com',
    phoneNumber: '1234567890',
    zipCode: TEST_UTILITIES.natGrid.zipCode,
    phone: '1234567890',
    originationSource: {
      originationSourceType: '',
      funnel: '',
    },
  },
  utility: fetchUtilityResponse('natGrid'),
  utilityAccount: {
    id: 4,
  },
  utilityCredential: {
    id: 5,
    verificationStatus: 'verified', // TODO: not a real status
  },
  funnelVnmProjectId: 12345,
};

const ILfunnelPacket: FetchFunnelPacketResponse = {
  nonLmi: true,
  inviteCode: 'testCode',
  account: {
    id: 2,
    businessName: undefined,
    customerType: 'residential',
  },
  customPlan: {
    id: 3,
    paymentMethodId: null,
  },
  planTemplate: {
    id: 4,
  },
  signedContractIds: [10, 11],
  user: {
    id: 1,
    promoCode: undefined,
    firstName: 'Freddie',
    lastName: 'Flexington',
    email: 'freddie@example.com',
    phoneNumber: '1234567890',
    zipCode: TEST_UTILITIES.ameren.zipCode,
    phone: '1234567890',
    originationSource: {
      originationSourceType: '',
      funnel: '',
    },
  },
  utility: fetchUtilityResponse('ameren'),
  utilityAccount: {
    id: 4,
  },
  utilityCredential: {
    id: 5,
    verificationStatus: 'verified', // TODO: not a real status
  },
};

const MDfunnelPacket: FetchFunnelPacketResponse = {
  nonLmi: true,
  inviteCode: 'testCode',
  account: {
    id: 2,
    businessName: undefined,
    customerType: 'residential',
  },
  customPlan: {
    id: 3,
    paymentMethodId: null,
  },
  planTemplate: {
    id: 4,
  },
  signedContractIds: [10, 11],
  user: {
    id: 1,
    promoCode: undefined,
    firstName: 'Freddie',
    lastName: 'Flexington',
    email: 'freddie@example.com',
    phoneNumber: '1234567890',
    zipCode: TEST_UTILITIES.ameren.zipCode,
    phone: '1234567890',
    originationSource: {
      originationSourceType: '',
      funnel: '',
    },
  },
  utility: fetchUtilityResponse('pepco'),
  utilityAccount: {
    id: 4,
  },
  utilityCredential: {
    id: 5,
    verificationStatus: 'verified', // TODO: not a real status
  },
};

export const TEST_DATA = Object.freeze({
  accounts: [funnelPacket.account],
  billingExperienceOptions: ['acb'] as BillingDataSource[],
  existingPlans: [] as CustomPlan[],
  credentials: {
    unverified: 1,
    correct: 2,
    incorrect: 3,
    error: 4,
  },
  docusignClickWrap: {
    clickwrapId: 'f8b0edeb-fdc9-4a68-9e6d-b9ba80ac3be7',
    docuSignAccountId: '84584bfe-6ad3-454a-978b-06d2c91869fa',
    email: 'donna_documents@arcadia.com',
    environment: 'https://demo.docusign.net',
    agreementId: null,
  } as Partial<DocuSignClickwrap>,
  googleUser: {
    email: 'new_user@example.com',
    firstName: 'Freddie',
    lastName: 'Flexington',
  },
  emails: {
    available: 'new_user@example.com',
    taken: 'existing_user@example.com',
  },
  regex: { email: /^cs-subscriber-intake(\+\d+)?@arcadia\.com$/ },
  enrollmentOptions: ['utility_connect', 'partner_provided'] as EnrollmentDataSource[],
  funnelPacket,
  ILfunnelPacket,
  MDfunnelPacket,
  funnelPacketLMIUsers,
  funnelPacketBusiness,
  billingOptionsPayload,
  fetchFunnelPacketWtPayment,
  lmiDocSubmit,
  lmiDocSkip,
  lmiUploadDoc,
  nonLmiUploadDoc,
  paymentMethods: [
    {
      id: 1,
      lastFour: '1234',
      source: 'Visa',
      type: 'credit_card',
      hasSuccessfulTransactions: false,
    },
    {
      id: 2,
      lastFour: '5678',
      source: 'Mastercard',
      type: 'credit_card',
      hasSuccessfulTransactions: false,
    },
  ],
  phone: {
    invalid: {
      formatted: '818500320',
      raw: '818-500-320',
    },
    valid: {
      formatted: '8185003200',
      raw: '818-500-3200',
    },
    validWithCountryDigit: {
      formatted: '8185003200',
      incompleteAndFormatted: '1818500320',
      raw: '1-818-500-3200',
    },
    twilioApiError: {
      formatted: '7185003200',
      raw: '718-500-3200',
    },
  },
  promoCodes: {
    partner: 'nextgoldman',
  },
  services: {
    agreeToTerms: undefined,
    uploadContracts: true,
    createFunnelPacket: funnelPacket,
    createMeter: {
      id: 10,
      meterNumber: '123',
      serviceType: 'electric',
      utilityAccountId: 4,
      utilityId: UTILITY_IDS['National Grid NY'],
    },
    createPaymentHold: {
      result: 'success',
    },
    createProductLead: {
      productLeadId: 11,
    },
    createUser: {
      userId: 1,
    },
    deleteBillUpload: {
      id: 1,
    },
    deleteLmiProofDocUpload: {
      id: 1,
    },
    fetchBillingExperienceOptions: {
      billingExperienceOptions: ['acb'],
    },
    fetchContractVersionLanguage: {
      id: 1,
      contractVersionLanguage: {
        id: 1,
      },
    },
    fetchExistingPlans: {
      existingPlans: [],
    },
    fetchBillpayEligibility: { billpayEligibile: true },
    fetchEnrollmentOptions: {
      enrollmentOptions: ['utility_connect', 'partner_provided'],
    },
    fetchExistingPaymentMethods: {
      existingPaymentMethods: [],
    },
    fetchExistingUtilityAccounts: {
      utilityAccounts: TEST_UTILITY_ACCOUNTS,
    },
    fetchFunnelPacket: funnelPacket,
    getUserDocusignEnvelopes: {
      user: {
        id: 123,
        userDocuSignEnvelopes: [
          {
            displayName: 'Attestation of Income-Based Eligibility',
            contractType: 'lmi',
            status: 'sent',
            completedFile: null,
            envelopeUpdatedAt: '2025-02-07',
            userId: '2831768',
            personalizedContractId: '327649',
          },
        ],
      },
    },
    fetchNyLmiZoneOptions: {
      dacZone: false,
      empowerZone: false,
      isAddressPresent: true,
    },
    fetchIsInLmiZone: {
      lmiZoneOptions: {
        isInLmiArea: true,
        error: false,
        isInCensusTract: false,
      },
    },
    fetchFunnelPacketWtPayment,
    fetchFunnelPacketUcb,
    fetchFunnelPacketAmerenMo,
    fetchDocuSignEnvelopes: { docuSignEnvelopes: [] },
    fetchMembershipPromoCode: {
      sponsored: false,
      sponsoredBy: '',
    },
    fetchPlanTemplate: {
      billingExperienceOptions: ['acb'],
      enrollmentOptions: ['utility_connect'],
      products: [
        { id: 2, productType: 'BillPayService' },
        { id: 1, productType: 'VNM' },
      ],
    },
    fetchStripeToken: {
      stripeToken: '123stripe',
    },
    fetchUtility: fetchUtilityResponse('natGrid'),
    fetchUtilityAccount: {
      utilityAccount: {
        id: 1,
        accountNumber: '123',
        serviceAddressStreetOne: '123 Main St',
        serviceAddressCity: 'New Jersey',
        serviceAddressState: 'NJ',
        serviceAddressZip: '02101',
      },
    },
    fetchUtilityConnectToken: {
      utilityConnectToken: '123abc',
    },
    fetchVnmTerms: {
      docuSignContracts: [],
      generalContracts: TEST_TERMS.general,
      vnmSolarProjectContracts: TEST_TERMS.standardSolar,
    },
    fetchWaitlistOptions: {
      funnelTrack: FunnelTrack.Waitlist,
      utilityScrapeEligible: true,
    },
    fetchWaitlistUtility: fetchUtilityResponse('pnm'),
    fetchVnmTermsLmi: {
      docuSignContracts: TEST_TERMS.lmi,
      generalContracts: TEST_TERMS.general,
      showLmi: true,
      vnmSolarProjectContracts: TEST_TERMS.standardSolar,
    },
    fetchLMIProofDocs: {
      lmiProofDocs: [
        {
          proofDocUrls: ['https://example.com'],
          proofDocName: 'example_proof_doc',
          proofDocStatus: 'example_status',
          uaLmiSourceTypeId: 1,
          utilityAccountId: 1,
          utilityAccountNumber: '123456',
          utilityName: 'example_utility',
        },
      ],
    },
    listenForPayment: {
      paymentMethod: {
        id: 1,
        lastFour: '1234',
        source: 'Visa',
        type: 'credit_card',
        hasSuccessfulTransactions: false,
      },
    },
    redirectToDashboard: undefined,
    requestDocuSignEnvelope: undefined,
    sendDocusignInstantAlert: {
      success: true,
    },
    sendInstantAlert: {
      success: true,
      message: 'success',
    },
    sendTelesalesEmail: { totalFailed: 0, totalSent: 1 },
    signUpUser: {
      plan: { id: 1, planTemplateId: 178 },
    },
    updateAmerenCredential: {
      verificationStatus: 'correct',
    },
    updatePlanPaymentMethod: {
      paymentMethod: {
        id: 2,
        lastFour: '5678',
        source: 'Mastercard',
        type: 'credit_card',
        hasSuccessfulTransactions: false,
      },
    },
    updateUserPreferences: undefined,
    updateUtilityAccount: undefined,
    updateOriginationSource: Promise.resolve(),
    uploadBillImage: { uploadId: 2 },
    uploadLMIProofDoc: {
      image: addFileProps(new File(['accepted'], 'accepted.png', { type: 'image/png' })),
      documentType: 'Medicaid',
    },
    updateConsolidatedUtilityAccount: {
      success: true,
    },
    updateUtilityAccountDetail: {
      utilityAccountDetailId: 1,
    },
  } as Services,
  states: TEST_STATES,
  terms: TEST_TERMS,
  // NB: these were generated from a testing site (not real arcadia token or signature)
  tokens: {
    // user id === 123
    valid:
      'eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJpc3MiOiJBUCBUZXN0IiwiaWF0IjoxNjIxMzgzMzE2LCJleHAiOjE4NDIzMDgxMTYsImF1ZCI6Ind3dy5leGFtcGxlLmNvbSIsInN1YiI6IlVzZXI6MTIzIn0.FsDDaKu5yB4_p_Plf25UPczIXQDVt118KuMJjvHLZDQ',
    // expired 05/21
    expired:
      'eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJpc3MiOiJBUCBUZXN0IiwiaWF0IjoxNjIxMzgzMzE2LCJleHAiOjE2MjEzODMzMTYsImF1ZCI6Ind3dy5leGFtcGxlLmNvbSIsInN1YiI6IlVzZXI6MTIzIn0.r7MjxAmaX9GLEhEpJzhdEbBV1dKdGhMrOZxj4bhxM6U',
    // no user id
    invalid:
      'eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJpc3MiOiJBUCBUZXN0IiwiaWF0IjoxNjIxMzgzMzE2LCJleHAiOjE2MjEzODMzMTYsImF1ZCI6Ind3dy5leGFtcGxlLmNvbSIsInN1YiI6IkJvYmJ5QnJ1bmNoYmlsbCJ9.9qseOTUHBYTvabJ9pRYNmAp_Q-_-soXLdTpQaj1RqoA',
  },
  utilities: TEST_UTILITIES,
  utilityAccounts: TEST_UTILITY_ACCOUNTS,
  customPlans: [
    {
      id: 1,
      active: true,
    },
  ],
  unsyncedUtilityAccount: {
    id: 1,
    accountNumber: null,
    displayServiceAddress: '',
    hasVnmProduct: false,
    utility: {
      id: 1,
      displayName: 'National Grid MA',
    },
  },
  utilityCredentials: [
    {
      id: 1,
      utilityName: 'NationalGrid',
      username: 'freddieflex123',
      verificationStatus: 'verified',
    },
    { id: 2, utilityName: 'ComEd', username: 'freddieflex123', verificationStatus: 'unverified' },
    { id: 3, utilityName: 'ComEd', username: 'freddieflex456', verificationStatus: 'incorrect' },
  ],
  docuSignContracts: [
    {
      __typename: 'ContractVersion',
      contractType: 'lmi',
      displayName: 'Subscriber Verification of Low to Moderate Income Status',
      fileUrl:
        'https://arcadiapowerdev.s3.amazonaws.com/uploads/contract_version/lmi/md-lmi-self-attestation-1-0.pdf',
      id: 78086,
      scrollRequired: false,
      tag: null,
      contractId: 764,
    },
  ],
  vnmSolarProjectContracts: [
    {
      __typename: 'ContractVersion',
      contractType: 'privacy',
      displayName: 'Privacy Policy',
      fileUrl:
        'https://arcadiapowerdev.s3.amazonaws.com/uploads/contract_version/privacy/privacy-policy-1-7.pdf',
      id: 77173,
      scrollRequired: false,
      tag: null,
      contractId: 31,
    },
    {
      __typename: 'ContractVersion',
      contractType: 'terms',
      displayName: 'General Terms of Service',
      fileUrl:
        'https://arcadiapowerdev.s3.amazonaws.com/uploads/contract_version/terms/terms-of-service-4-7.pdf',
      id: 77174,
      scrollRequired: false,
      tag: null,
      contractId: 43,
    },
  ],
});

type Services = {
  agreeToTerms: AgreeToTermsResponse;
  createFunnelPacket: CreateFunnelPacketResponse;
  createMeter: CreateMeterResponse;
  createPaymentHold: CreatePaymentHoldResponse;
  createProductLead: CreateProductLeadResponse;
  createUser: CreateUserResponse;
  deleteBillUpload: DeleteBillUploadResponse;
  deleteLmiProofDocUpload: DeleteLMIProofDocUploadResponse;
  fetchBillingExperienceOptions: FetchBillingExperienceOptionsResponse;
  fetchExistingPlans: FetchExistingPlansResponse;
  fetchBillpayEligibility: FetchBillpayEligibilityResponse;
  fetchContractVersionLanguage: FetchContractLanguageResponse;
  fetchDocuSignEnvelopes: FetchDocuSignEnvelopesResponse;
  fetchEnrollmentOptions: FetchEnrollmentOptionsResponse;
  fetchExistingPaymentMethods: FetchExistingPaymentMethodsResponse;
  fetchExistingUtilityAccounts: FetchExistingUtilityAccountsResponse;
  fetchFunnelPacket: FetchFunnelPacketResponse;
  fetchNyLmiZoneOptions: FetchNyLmiZoneOptionsResponse;
  fetchIsInLmiZone: FetchIsInLmiZoneResponse;
  fetchFunnelPacketWtPayment: FetchFunnelPacketResponse;
  fetchFunnelPacketUcb: FetchFunnelPacketResponse;
  fetchFunnelPacketAmerenMo: FetchFunnelPacketResponse;
  fetchMembershipPromoCode: FetchMembershipPromoCodeResponse;
  fetchPlanTemplate: FetchPlanTemplateResponse;
  fetchStripeToken: FetchStripeTokenResponse;
  fetchUtility: FetchUtilityResponse;
  fetchUtilityAccount: FetchUtilityAccountResponse;
  fetchUtilityConnectToken: FetchUtilityConnectTokenResponse;
  fetchVnmTerms: FetchVnmTermsResponse;
  fetchWaitlistOptions: FetchWaitlistOptionsResponse;
  fetchWaitlistUtility: FetchUtilityResponse;
  fetchVnmTermsLmi: FetchVnmTermsResponse;
  fetchLMIProofDocs: FetchLMIProofDocsResponse;
  listenForPayment: ListenForPaymentResponse;
  redirectToDashboard: RedirectToDashboardResponse;
  requestDocuSignEnvelope: RequestDocuSignEnvelopeResponse;
  sendDocusignInstantAlert: SendDocusignInstantAlertResponse;
  sendInstantAlert: SendInstantAlertResponse;
  sendTelesalesEmail: SendTelesalesEmailServiceResponse;
  signUpUser: SignUpUserResponse;
  updateAmerenCredential: UpdateAmerenCredentialResponse;
  updatePlanPaymentMethod: UpdatePlanPaymentMethodResponse;
  updateUserPreferences: UpdateUserPreferencesResponse;
  updateUtilityAccount: UpdateUtilityAccountResponse;
  uploadBillImage: UploadBillImageResponse;
  uploadLMIProofDoc: LMIProofDoc;
  uploadContracts: UploadContractsResponse;
  updateConsolidatedUtilityAccount: UpdateConsolidatedUtilityAccountResponse;
  updateUtilityAccountDetail: UpdateUtilityAccountDetailResponse;
  updateOriginationSource: Promise<void>;
  getUserDocusignEnvelopes: GetUserDocuSignEnvelopesQuery;
};
